/**
 * Display Boards — public "now serving" displays for queuing (Queues-section
 * tab, route `queues.boards`). The admin composes a board (which queues, a
 * layout, a theme, how queuers are identified, a welcome message) and puts its
 * public URL on a lobby screen. List paradigm: List/Cards toggle, search +
 * layout filter, sortable columns, Open · Edit · Clone · ⋯ per item (⋯: copy
 * display link, delete). Writes AWAIT saveMessageBoard / deleteMessageBoard.
 *
 * AUTHORING lives in the Journey Designer (2026-08-26): Add Board opens
 * `newjourneys?new=board`, Edit opens `newjourneys?journey=board:<id>`. The
 * old full-screen QueueBoardBuilder was deleted — this screen is the library
 * (view · edit · clone · delete · display link), not an editor.
 */
import { useMemo, useState, type CSSProperties } from "react";
import { Btn, Icon, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { FilterBar, FilterDropdown, EmptyState, RowActions } from "../../shell/composites";
import { useViewMode, ViewToggle, CardActionBar, CARD_GRID } from "../../shell/list-composites";
import { useIsMobile } from "../../shell/useResponsive";
import { useNav } from "../../shell/router";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import { useWfResource, useBusy } from "../workforce/useWorkforce";
import { WfPending } from "../workforce/wf-shared";
import { listMessageBoards, saveMessageBoard, deleteMessageBoard, mintBoardDisplayUrl, listQueues, type MessageBoard, type QueueSummary, type QueueListPage } from "./queues-api";
import { queuesSectionTabs, useQueueSectionCounts, SORT_ARROW } from "./queues-section";
import { useTenantPublicConfig } from "../../lib/public-url";
import { copyText } from "../../lib/copy-text";
import { boardPublicUrl } from "./board-blocks";

import { useT } from "@v0y4ge/i18n-runtime";
const layoutLabel = (t: (key: string, fallback: string) => string): Record<MessageBoard["layout"], string> => ({ "now-serving": t("queues.messageBoards.layoutNowServing", "Now serving"), "full-list": t("queues.messageBoards.layoutFullList", "Full list") });
const thStyle: CSSProperties = { padding: "10px 16px", textAlign: "left", fontWeight: 600, fontSize: 12, color: T.soft, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none" };
const tdStyle: CSSProperties = { padding: "12px 16px", verticalAlign: "middle", fontSize: 13, color: T.n2, borderBottom: `1px solid ${T.border}` };

export default function QueueMessageBoards() {
  const t = useT();
  const LAYOUT_LABEL = layoutLabel(t);
  const nav = useNav();
  const isMobile = useIsMobile();
  const counts = useQueueSectionCounts();
  const writable = can("queues.write");
  const { data, loading, error, reload } = useWfResource<{ boards: MessageBoard[] }>("qboards", () => listMessageBoards());
  const { data: queuesData } = useWfResource<QueueListPage>("queues|all", () => listQueues(null));
  const { run } = useBusy();
  const [view, setView] = useViewMode("qboards", "grid");
  const [query, setQuery] = useState("");
  const [layoutFilter, setLayoutFilter] = useState<string | null>(null);
  const [sort, setSort] = useState("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  useTenantPublicConfig(); // warm the tenant public-host cache for the display links

  const queues = queuesData?.data ?? [];
  const queueName = (id: string) => queues.find((q) => q.id === id)?.name ?? id;

  const onHeaderSort = (k: string) => {
    if (sort === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSort(k); setSortDir("asc"); }
  };
  const rows = useMemo(() => {
    const qy = query.trim().toLowerCase();
    let out = (data?.boards ?? []).filter((b) =>
      (!qy || b.name.toLowerCase().includes(qy) || b.message.toLowerCase().includes(qy)) &&
      (!layoutFilter || LAYOUT_LABEL[b.layout] === layoutFilter));
    const val = (b: MessageBoard): string | number => sort === "layout" ? b.layout : sort === "queues" ? b.queueIds.length : sort === "updated" ? b.updatedAt : b.name.toLowerCase();
    out = [...out].sort((a, b) => { const x = val(a), y = val(b); const base = x < y ? -1 : x > y ? 1 : 0; return sortDir === "desc" ? -base : base; });
    return out;
  }, [data, query, layoutFilter, sort, sortDir]);

  // The lobby display is token-authorized: mint the board's signed display
  // token, then open/share the URL carrying it. Opening `/queue/display?b=<id>`
  // with no token renders the fail-closed 403 banner, which is what the
  // preview (eye) affordance used to do.
  const openDisplay = (b: MessageBoard) => run(`disp:${b.id}`, async () => {
    const minted = await mintBoardDisplayUrl(b.id);
    nav.go("queue.display", { b: b.id, t: minted.token });
  });
  const copyLink = async (b: MessageBoard) => { // ui-local — clipboard only
    let url = boardPublicUrl(b.id);
    try {
      const minted = await mintBoardDisplayUrl(b.id);
      url += `${url.includes("?") ? "&" : "?"}t=${encodeURIComponent(minted.token)}`;
    } catch {
      toast({ tone: "danger", title: t("queues.messageBoards.toastMintFailedTitle", "Couldn't create a display link"), subtitle: t("queues.messageBoards.toastMintFailedSubtitle", "Publishing a display link needs the queues publish permission.") });
      return;
    }
    if (await copyText(url)) toast({ tone: "success", title: t("queues.messageBoards.toastLinkCopiedTitle", "Display link copied"), subtitle: t("queues.messageBoards.toastLinkCopiedSubtitle", "Open it full-screen on the lobby screen.") });
    else toast({ tone: "danger", title: t("queues.messageBoards.toastCopyFailedTitle", "Couldn't copy automatically"), subtitle: url });
  };
  const remove = (b: MessageBoard) => run(`del:${b.id}`, async () => {
    await deleteMessageBoard(b.id);
    toast({ tone: "success", title: t("queues.messageBoards.toastDeletedTitle", "Board deleted"), subtitle: t("queues.messageBoards.toastDeletedSubtitle", "“{name}” removed.").replace("{name}", b.name) }); // ui-config — "{…}" strings are message-placeholder tokens, not copy
    reload();
  });
  const clone = (b: MessageBoard) => run(`cl:${b.id}`, async () => {
    await saveMessageBoard({ ...b, id: `mb_${Date.now().toString(36)}`, name: `${b.name} (copy)` });
    toast({ tone: "success", title: t("queues.messageBoards.toastClonedTitle", "Board cloned"), subtitle: t("queues.messageBoards.toastClonedSubtitle", "“{name} (copy)” created.").replace("{name}", b.name) }); // ui-config — "{…}" strings are message-placeholder tokens, not copy
    reload();
  });

  /** Edit = the Journey Designer, opened on this board's own journey document
   *  (`board:<id>`, the key docFromBoard mints). Setup · Configuration there is
   *  what the old builder was. */
  const editBoard = (b: MessageBoard) => nav.go("newjourneys", { journey: `board:${b.id}` });

  const actions = (b: MessageBoard) => ({
    onView: () => openDisplay(b),
    viewLabel: t("queues.messageBoards.actionOpenDisplay", "Open public display"),
    onEdit: writable ? () => editBoard(b) : undefined,
    editLabel: t("queues.messageBoards.actionEditBoard", "Edit board"),
    onDuplicate: writable ? () => clone(b) : undefined,
    duplicateLabel: t("queues.messageBoards.actionCloneBoard", "Clone board"),
    moreHeader: b.name,
    moreItems: [
      { icon: <Icon.copy size={14} />, label: t("queues.messageBoards.menuCopyDisplayLink", "Copy display link"), onClick: () => copyLink(b) },
      ...(writable ? ["divider" as const, { icon: <Icon.trash size={14} />, label: t("queues.messageBoards.menuDeleteBoard", "Delete board"), danger: true, onClick: () => remove(b) }] : []),
    ],
  });

  const filtersActive = !!(query || layoutFilter);
  const listView = (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
        <thead>
          <tr>
            {[["name", t("queues.messageBoards.colBoard", "Board")], ["layout", t("queues.messageBoards.colLayout", "Layout")], ["queues", t("queues.messageBoards.colQueues", "Queues")], ["updated", t("queues.messageBoards.colUpdated", "Updated")]].map(([k, label]) => (
              <th key={k} style={thStyle} onClick={() => onHeaderSort(k)} aria-sort={sort === k ? (sortDir === "asc" ? "ascending" : "descending") : undefined}>{label}{SORT_ARROW(sort === k, sortDir)}</th>
            ))}
            <th style={{ ...thStyle, cursor: "default", textAlign: "right" }}>{t('queues.messageBoards.text')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => (
            <tr key={b.id} data-testid="qboards-table-row" data-id={b.id} style={{ cursor: "pointer" }} onClick={() => writable ? editBoard(b) : nav.go("queue.display", { b: b.id })}>
              <td style={{ ...tdStyle, fontWeight: 700, color: T.n1 }}>
                {b.name}
                <div style={{ fontSize: 11.5, fontWeight: 400, color: T.soft, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 320 }}>{b.message}</div>
              </td>
              <td style={tdStyle}><Pill tone={b.layout === "now-serving" ? "primary" : "info"}>{LAYOUT_LABEL[b.layout]}</Pill> <Pill tone="neutral">{b.theme}</Pill></td>
              <td style={tdStyle}>{b.queueIds.map(queueName).join(", ")}</td>
              <td style={tdStyle}>{new Date(b.updatedAt).toLocaleDateString([], { month: "short", day: "numeric" })}</td>
              <td style={tdStyle} onClick={(e) => e.stopPropagation()}><RowActions {...actions(b)} testid="qboards-row" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const cardView = (
    <div style={{ display: "grid", gridTemplateColumns: CARD_GRID, gap: 14 }}>
      {rows.map((b) => (
        <div key={b.id} data-testid="qboards-card" data-id={b.id}
          style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, boxShadow: T.shadowSm, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          {/* mini display preview */}
          <div style={{ background: b.theme === "dark" ? "#14131F" : "#F4F5F8", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: .08, textTransform: "uppercase", color: b.theme === "dark" ? "rgba(255,255,255,.55)" : T.soft }}>{t('queues.messageBoards.text2')}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: b.theme === "dark" ? "#fff" : T.n1 }}>{t('queues.messageBoards.text3')}</div>
            <div style={{ fontSize: 11, color: b.theme === "dark" ? "rgba(255,255,255,.6)" : T.soft }}>{b.queueIds.slice(0, 2).map(queueName).join(" · ")}{b.queueIds.length > 2 ? /* TODO(i18n): replace template-literal with t('queues.messageBoards.text4', { ... }) */` +${b.queueIds.length - 2}` : ""}</div>
          </div>
          <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 14.5, fontWeight: 700, color: T.n1, flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{b.name}</span>
              <Pill tone={b.layout === "now-serving" ? "primary" : "info"}>{LAYOUT_LABEL[b.layout]}</Pill>
            </div>
            <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.5 }}>{b.message}</div>
          </div>
          <CardActionBar left={<span>{b.queueIds.length} {t('queues.messageBoards.text5')}{" "}{b.queueIds.length === 1 ? "" : t('queues.messageBoards.text6')}</span>} {...actions(b)} testid="qboards-card" />
        </div>
      ))}
    </div>
  );

  return (
    <Screen rail="q-board" tabs={queuesSectionTabs(nav, "boards", counts)} activeTab="boards">
      <PageHeader title={t('queues.messageBoards.title5')} subtitle={t('queues.messageBoards.subtitle2')} breadcrumb={null}
        actions={<>
          <ViewToggle mode={view} onChange={setView} testid="qboards-view" />
          {/* Add Board (2026-08-25) — a new board is authored in the Journey
              designer (`newjourneys?new=board`), the same surface Edit now opens
              for an existing row. */}
          {writable ? <Btn icon={<Icon.plus size={14}/>} onClick={() => nav.go("newjourneys", { new: "board" })} testid="qboards-new-btn">{t('queues.messageBoards.text7')}</Btn> : null}
        </>} />
      <DataSourceStrip screen="qboards" ops={["listMessageBoards", "saveMessageBoard", "deleteMessageBoard", "listQueues"]} />
      <FilterBar>
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('queues.messageBoards.placeholder')} aria-label={t('queues.messageBoards.ariaLabel')} data-testid="qboards-search-input"
          style={{ height: 34, padding: "0 12px", border: `1px solid ${T.n4}`, borderRadius: 8, fontSize: 13, fontFamily: T.font, width: 220 }} />
        <FilterDropdown label={layoutFilter || t("queues.messageBoards.filterAnyLayout", "Any layout")} active={!!layoutFilter} options={Object.values(LAYOUT_LABEL)} onPick={setLayoutFilter} onClear={() => setLayoutFilter(null)} />
        {filtersActive ? <button onClick={() => { setQuery(""); setLayoutFilter(null); }} style={{ padding: "6px 10px", borderRadius: 999, fontSize: 12, fontWeight: 500, background: "transparent", color: T.p1, border: "none", cursor: "pointer", fontFamily: T.font }}>{t('queues.messageBoards.text8')}</button> : null}
      </FilterBar>
      <div style={{ padding: "0 24px 24px", flex: 1, overflow: "auto" }}>
        {error ? <WfPending screen="qboards" opName="listMessageBoards" error={error} onRetry={reload} />
        : loading ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t('queues.messageBoards.text9')}</div>
        : rows.length === 0 ? (
          filtersActive
            ? <EmptyState icon={<Icon.browser size={20}/>} title={t("queues.messageBoards.emptyFilteredTitle", "No boards match")} body={t('queues.messageBoards.body', 'Try clearing the search or layout filter.')} action={<Btn variant="secondary" onClick={() => { setQuery(""); setLayoutFilter(null); }}>{t("queues.messageBoards.text8", "Clear filters")}</Btn>} />
            : <EmptyState icon={<Icon.browser size={20}/>} title={t("queues.messageBoards.emptyNoneTitle", "No display boards yet")} body={t("queues.messageBoards.emptyNoneBody", "Create a board, pick which queues it shows, and open its public link on the lobby screen.")} action={writable ? <Btn onClick={() => nav.go("newjourneys", { new: "board" })}>{t("queues.messageBoards.text7", "Add Board")}</Btn> : undefined} />
        ) : view === "list" && !isMobile ? listView : cardView}
      </div>
    </Screen>
  );
}
